import type {Logger as PinoLogger} from 'pino';
import Pino from 'pino';

import {
    createTextRingBuffer,
    type TextRingBuffer,
    type OnSegmentSealed,
} from '@pexip/utils';

import {LogLevels, type Logger, type LogLevelsString} from './baseLogger';
import {createRedactor, type Redactor} from './redactor';
export * from './redactor';

const REDACTED_REPLACEMENT = '[REDACTED]';

/**
 * Default amount of memory, in bytes, used to retain log events.
 */
const DEFAULT_MAX_LOG_BYTES = 4 * 1024 * 1024;
/**
 * Default maximum number of log events retained in memory.
 */
const DEFAULT_MAX_LOG_LINES = 16384;
/**
 * Number of segments the log memory is split into. Log events are dropped a
 * segment at a time, so this is also the granularity at which memory is
 * reclaimed.
 */
const LOG_SEGMENT_COUNT = 8;
const textEncoder = new TextEncoder();
const MIN_TRUNCATED_LOG_EVENT = '{"logEventTruncated":true}\n';
const MIN_LOG_BYTE_BUDGET =
    textEncoder.encode(MIN_TRUNCATED_LOG_EVENT).byteLength * LOG_SEGMENT_COUNT;
const SERIALIZATION_FAILURE_EVENT =
    '{"msg":"Log event could not be serialized"}';

export type {Logger, LogLevelsString};
export {LogLevels};
export interface ChildLogger extends Logger {
    child(bindings: Record<string, unknown>): Logger;
}

export interface PexLogger extends ChildLogger {
    /**
     * Set this property to the desired logging level. In order of priority, available levels are:
        1. trace
        2. debug
        3. info
        4. warn
        5. error
        6. fatal
        7. silent

        The logging level is a minimum level. For instance if logger.level is 'info' then all 'fatal', 'error', 'warn', and 'info' logs will be enabled.

        You can pass 'silent' to disable logging.
     */
    level: string;
    /**
     * Get the log file as a blob
     */
    getLogs(): Blob;
    /**
     * Get a file name based on (app) name, current date/time, and extension
     */
    getLogFileName(): string;
    /**
     * Get the log file as a string.
     */
    getLogFile(): string;
    /**
     * The log events retained in memory.
     *
     * The buffer is bounded, see `maxLogBytes` and `maxLogLines`, so the oldest
     * events are dropped once it is full. It can be read lazily, one event at a
     * time, by iterating over it.
     **/
    logEvents: TextRingBuffer;
    /**
     * Create a log file based on the log until now, and trigger a download
     */
    downloadLog(): void;
}

export function stringifyReplacer(_key: string, value: unknown) {
    if (value instanceof HTMLElement) {
        const innerHTML = value.innerHTML;
        if (innerHTML !== '') {
            return value.outerHTML.replace(innerHTML, '{...}');
        } else {
            return value.outerHTML;
        }
    } else if (value instanceof Function) {
        return `[Function ${value.name}]`;
    }
    return value;
}

interface Options {
    /**
     * The name of the logger. When set adds a name field to every JSON line logged
     */
    name?: string;
    /**
     * Minimum level to be logged
     */
    minLevel?: LogLevelsString;
    /**
     * Name to prefix the file with for the output log file
     * @defaultValue 'pexip'
     */
    fileName?: string;
    /**
     * Extension (without the dot) to use for the output log file
     * @defaultValue 'log'
     */
    fileExt?: string;

    redactor?: Redactor;

    /**
     * Maximum amount of memory, in bytes, used to retain log events. Once
     * reached, the oldest events are dropped.
     *
     * The budget is split into equally sized blocks which are dropped as a
     * whole, so a single event larger than an eighth of it is replaced with a
     * bounded JSON record containing truncation metadata.
     *
     * Set to `0` to not retain log events at all, see {@link maxLogLines}.
     * @defaultValue 4194304 (4 MiB)
     */
    maxLogBytes?: number;
    /**
     * Maximum number of log events retained in memory. Once reached, the oldest
     * events are dropped.
     *
     * Set this or {@link maxLogBytes} to `0` for consumers which only want
     * events written to the console: no buffer is allocated and events are
     * neither serialized nor redacted, so `onLogSegmentSealed` is never called,
     * `logEvents` behaves as a permanently empty buffer, and `downloadLog`
     * produces an empty file.
     * @defaultValue 16384
     */
    maxLogLines?: number;
    /**
     * Called when a block of log events is about to leave the active part of
     * the buffer, which is the extension seam for persisting them elsewhere.
     * @see {@link OnSegmentSealed}
     */
    onLogSegmentSealed?: OnSegmentSealed;
}

export const isLogLevelKey = (t: string | undefined): t is LogLevelsString =>
    typeof t === 'string' && t in LogLevels;

const assertLogBudget = (
    name: string,
    value: number,
    minimum = LOG_SEGMENT_COUNT,
) => {
    if (!Number.isInteger(value) || (value !== 0 && value < minimum)) {
        throw new Error(
            `${name} must be 0, to not retain log events, or an integer of at least ${minimum}`,
        );
    }
};

const createTruncatedLogEvent = (
    event: Record<string, unknown>,
    originalByteLength: number,
    maximumByteLength: number,
) => {
    const level = typeof event.level === 'number' ? event.level : undefined;
    const time = typeof event.time === 'number' ? event.time : undefined;
    const replacement = `${JSON.stringify({
        level,
        time,
        msg: 'Log event exceeded maximum size',
        logEventTruncated: true,
        originalByteLength,
        maximumByteLength,
    })}\n`;

    return textEncoder.encode(replacement).byteLength <= maximumByteLength
        ? replacement
        : MIN_TRUNCATED_LOG_EVENT;
};

const utf8ByteLength = (value: string) => {
    let byteLength = 0;
    for (let index = 0; index < value.length; index++) {
        const codeUnit = value.charCodeAt(index);
        if (codeUnit <= 0x7f) {
            byteLength += 1;
        } else if (codeUnit <= 0x7ff) {
            byteLength += 2;
        } else if (
            codeUnit >= 0xd800 &&
            codeUnit <= 0xdbff &&
            index + 1 < value.length
        ) {
            const nextCodeUnit = value.charCodeAt(index + 1);
            if (nextCodeUnit >= 0xdc00 && nextCodeUnit <= 0xdfff) {
                byteLength += 4;
                index += 1;
                continue;
            }
            byteLength += 3;
        } else {
            byteLength += 3;
        }
    }
    return byteLength;
};

/**
 * Creates a buffer which discards everything written to it, used when log
 * events are not retained so that `logEvents` keeps its shape without
 * allocating any memory.
 *
 * Example usage:
 *
 * ```ts
 * const logEvents = createDiscardingLogEvents();
 * logEvents.push('{"msg":"hello"}\n');
 * logEvents.isEmpty(); // true
 * ```
 *
 * @returns An empty, immutable {@link TextRingBuffer}
 */
const createDiscardingLogEvents = (): TextRingBuffer => ({
    push: () => undefined,
    size: () => 0,
    byteLength: () => 0,
    isEmpty: () => true,
    droppedLines: () => 0,
    truncatedLines: () => 0,
    clear: () => undefined,
    flush: () => undefined,
    toBlobParts: () => [],
    toString: () => '',
    toArray: () => [],
    [Symbol.iterator]: () => [][Symbol.iterator](),
});

const stringifyLogEvent = (
    event: Record<string, unknown>,
    replacer?: (key: string, value: unknown) => unknown,
) => {
    const serializableEvent =
        typeof event.toJSON === 'function'
            ? Object.fromEntries(
                  Object.entries(event).filter(([key]) => key !== 'toJSON'),
              )
            : event;
    const serialized = JSON.stringify(serializableEvent, replacer);
    return typeof serialized === 'string' ? serialized : undefined;
};

export const createLogger = ({
    minLevel = 'info',
    fileName = 'pexip',
    fileExt = 'log',
    maxLogBytes = DEFAULT_MAX_LOG_BYTES,
    maxLogLines = DEFAULT_MAX_LOG_LINES,
    onLogSegmentSealed,
    redactor = createRedactor({
        defaultReplaceTo: REDACTED_REPLACEMENT,
        keysToRedact: [],
    }),
}: Options = {}): PexLogger => {
    assertLogBudget('maxLogBytes', maxLogBytes, MIN_LOG_BYTE_BUDGET);
    assertLogBudget('maxLogLines', maxLogLines);

    const retainLogEvents = maxLogBytes > 0 && maxLogLines > 0;
    const maxLogEventBytes = Math.floor(maxLogBytes / LOG_SEGMENT_COUNT);
    const logEvents = retainLogEvents
        ? createTextRingBuffer({
              segmentByteSize: maxLogEventBytes,
              segmentCount: LOG_SEGMENT_COUNT,
              maxLinesPerSegment: Math.floor(maxLogLines / LOG_SEGMENT_COUNT),
              onSegmentSealed: onLogSegmentSealed,
          })
        : createDiscardingLogEvents();

    function getLogs() {
        return new Blob(
            logEvents.toBlobParts(),
            // Technically application/ld+json
            {type: 'text/plain; charset=utf-8'},
        );
    }

    function getLogFile(): string {
        return logEvents.toString();
    }

    function getLogFileName(name = fileName, ext = fileExt) {
        return `${name}-${new Date().toISOString().replace(/:/g, '-')}.${ext}`;
    }

    function downloadLog(fileName = getLogFileName()) {
        const blobUrl = URL.createObjectURL(getLogs());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.click();
        // Wait half a second, as reportedly some browsers will fail if it's revoked too quickly
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    }

    // HACK: Stupid solution, but seems to be the best way to transform
    //       transmit format to what e.g. pino-pretty expects.
    const pinoStore = Pino({
        browser: {
            write: (evt: unknown) => {
                if (typeof evt !== 'object' || !evt) {
                    return;
                }
                let redactedEvent: Record<string, unknown>;
                let logEvent: string | undefined;
                try {
                    redactedEvent = redactor.redactObj(
                        evt as Record<string, unknown>,
                    );
                    logEvent = stringifyLogEvent(
                        redactedEvent,
                        stringifyReplacer,
                    );
                } catch {
                    logEvent = undefined;
                    redactedEvent = {};
                }
                if (logEvent === undefined) {
                    try {
                        redactedEvent = redactor.redactObj(
                            Object.fromEntries(
                                Object.entries(evt).map(([k, v]) => [
                                    k,
                                    typeof v === 'object' ? `${v}` : v,
                                ]),
                            ),
                        );
                        logEvent = stringifyLogEvent(redactedEvent);
                    } catch {
                        logEvent = undefined;
                        redactedEvent = {};
                    }
                }
                logEvent ??= SERIALIZATION_FAILURE_EVENT;
                const logLine = `${logEvent}\n`;
                const logLineByteLength = utf8ByteLength(logLine);
                if (logLineByteLength <= maxLogEventBytes) {
                    logEvents.push(logLine);
                } else {
                    logEvents.push(
                        createTruncatedLogEvent(
                            redactedEvent,
                            logLineByteLength,
                            maxLogEventBytes,
                        ),
                        true,
                    );
                }
            },
        },
        level: minLevel,
    });

    const pino = Pino({
        // Without `transmit` pino only writes to the console, which is exactly
        // what is wanted when events are not retained.
        browser: retainLogEvents
            ? {
                  transmit: {
                      send: (level, event) => {
                          const [first, ...rest] = event.messages; // work around ts typechecking
                          event.bindings
                              .reduce<Pino.Logger>(
                                  (ch, bi) => ch.child(bi),
                                  pinoStore,
                              )
                              [level](first, ...rest);
                      },
                  },
              }
            : {},
        level: minLevel,
    });

    const addPattern = (value: string, redactTo = REDACTED_REPLACEMENT) =>
        redactor.addPattern(value, redactTo);

    return {
        get logEvents() {
            return logEvents;
        },
        set level(level: typeof pino.level) {
            pinoStore.level = level;
            pino.level = level;
        },
        getLogs,
        getLogFileName,
        getLogFile,
        downloadLog,
        addRedactPattern: addPattern,
        redact: redactor.redactStr,
        fatal: (meta, message) => pino.fatal(meta, message),
        error: (meta, message) => pino.error(meta, message),
        warn: (meta, message) => pino.warn(meta, message),
        info: (meta, message) => pino.info(meta, message),
        debug: (meta, message) => pino.debug(meta, message),
        trace: (meta, message) => pino.trace(meta, message),
        silent: (meta, message) => pino.silent(meta, message),
        child: (...args: Parameters<PinoLogger['child']>) => {
            const child = pino.child(...args);
            return {
                fatal: (meta, message) => child.fatal(meta, message),
                error: (meta, message) => child.error(meta, message),
                warn: (meta, message) => child.warn(meta, message),
                info: (meta, message) => child.info(meta, message),
                debug: (meta, message) => child.debug(meta, message),
                trace: (meta, message) => child.trace(meta, message),
                silent: (meta, message) => child.silent(meta, message),
                redact: redactor.redactStr,
                addRedactPattern: addPattern,
            };
        },
    };
};
